// Analysis Route — FREEZE FIX: All heavy components are React.lazy() + Suspense
// This ensures the page shell renders INSTANTLY and charts load progressively.
import { createFileRoute } from '@tanstack/react-router'
import { Activity, BarChart2, CalendarDays, Eye } from 'lucide-react'
import React, { Suspense, useCallback, useEffect, useMemo, useState, useTransition, useDeferredValue } from 'react'
import { z } from 'zod'
import FullPageLoader from '../components/ui/FullPageLoader'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useSettingsStore } from '../stores/settingsStore'
import { AnalysisMode, IndexSymbol } from '../types'

// 🚀 FREEZE FIX: Lazy-load ALL heavy components.
// Previously these were statically imported, meaning the browser had to parse
// and mount CombinedAnalysisSection (385 lines) + IndividualAnalysisSection (407 lines)
// + MarketWatchSection (1968 lines) + StrikePriceHistoryTable (901 lines)
// ALL synchronously during the initial analysis route mount = guaranteed freeze.
const CombinedAnalysisSection = React.lazy(() =>
    import('../components/analysis/CombinedAnalysisSection').then(m => ({ default: m.CombinedAnalysisSection }))
)
const IndividualAnalysisSection = React.lazy(() =>
    import('../components/analysis/IndividualAnalysisSection').then(m => ({ default: m.IndividualAnalysisSection }))
)
const MarketWatchSection = React.lazy(() =>
    import('../components/analysis/MarketWatchSection').then(m => ({ default: m.MarketWatchSection }))
)
const StrikePriceHistoryTable = React.lazy(() =>
    import('../components/inspector/StrikePriceHistoryTable').then(m => ({ default: m.StrikePriceHistoryTable }))
)

// Lightweight loading skeleton — renders instantly, no JS computation
const ChartSkeleton = () => (
    <div className="space-y-6 animate-pulse">
        <div className="h-[300px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 border border-slate-200/50 dark:border-slate-700/50" />
        <div className="h-[300px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 border border-slate-200/50 dark:border-slate-700/50" />
    </div>
)

const TableSkeleton = () => (
    <div className="animate-pulse">
        <div className="min-h-[500px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 border border-slate-200/50 dark:border-slate-700/50" />
    </div>
)

const analysisSearchSchema = z.object({
    symbol: z.nativeEnum(IndexSymbol).optional(),
    expiry: z.string().optional(),
    mode: z.nativeEnum(AnalysisMode).optional(),
    ceStrike: z.number().optional(),
    peStrike: z.number().optional(),
})

export const Route = createFileRoute('/_auth/analysis')({
    validateSearch: (search) => analysisSearchSchema.parse(search),
    component: AnalysisRoute,
})

type AnalysisTab = 'Bars' | 'Weekly' | 'Market Watch'

function AnalysisRoute() {
    const { symbol: searchSymbol, expiry: searchExpiry } = Route.useSearch()

    usePageTitle('Analysis')

    const settingsSymbol = useSettingsStore((state) => state.symbol)
    const settingsExpiry = useSettingsStore((state) => state.expiry)
    const setSymbol = useSettingsStore((state) => state.setSymbol)
    const setExpiry = useSettingsStore((state) => state.setExpiry)

    // FREEZE FIX: Defer the symbol and expiry to prevent Zustand from forcing a synchronous re-render of heavy components
    const deferredSymbol = useDeferredValue(settingsSymbol)
    const deferredExpiry = useDeferredValue(settingsExpiry)

    const { isLoading: loading, marketData, effectiveExpiry } = useDashboardData()
    const deferredMarketData = useDeferredValue(marketData)

    // Sync search params with settings
    useEffect(() => {
        if (searchSymbol && searchSymbol !== settingsSymbol) {
            setSymbol(searchSymbol)
        }
        if (searchExpiry && searchExpiry !== settingsExpiry) {
            setExpiry(searchExpiry)
        }
    }, [searchSymbol, searchExpiry, settingsSymbol, settingsExpiry, setSymbol, setExpiry])

    // State for strikes specific to Analysis page
    const [dailyCeStrike, setDailyCeStrike] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`analysis_${deferredSymbol}_ce_strike`)
            if (saved) return parseInt(saved, 10) || 0
        }
        return 0
    })

    const [dailyPeStrike, setDailyPeStrike] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`analysis_${settingsSymbol}_pe_strike`)
            if (saved) return parseInt(saved, 10) || 0
        }
        return 0
    })

    // Active Tab State
    const [isPending, startTransition] = useTransition()
    const [activeTab, setActiveTab] = useState<AnalysisTab>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('analysis_active_tab') as AnalysisTab
            if (saved && ['Bars', 'Weekly', 'Market Watch'].includes(saved)) return saved
        }
        return 'Bars'
    })

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('analysis_active_tab', activeTab)
        }
    }, [activeTab])

    // Sync from Inspector daily strikes for smooth transition if needed,
    // or just reset on symbol change
    useEffect(() => {
        if (typeof window === 'undefined') return
        const sym = settingsSymbol

        const savedDailyCe = localStorage.getItem(`analysis_${sym}_ce_strike`)
        setDailyCeStrike(savedDailyCe ? parseInt(savedDailyCe) : 0)

        const savedDailyPe = localStorage.getItem(`analysis_${deferredSymbol}_pe_strike`)
        setDailyPeStrike(savedDailyPe ? parseInt(savedDailyPe) : 0)
    }, [deferredSymbol])

    // Memoize callbacks to prevent breaking React.memo on child components
    const handleDailyCeStrikeChange = useCallback((v: number) => {
        setDailyCeStrike(v)
        if (typeof window !== 'undefined') localStorage.setItem(`analysis_${deferredSymbol}_ce_strike`, String(v))
    }, [deferredSymbol])

    const handleDailyPeStrikeChange = useCallback((v: number) => {
        setDailyPeStrike(v)
        if (typeof window !== 'undefined') localStorage.setItem(`analysis_${deferredSymbol}_pe_strike`, String(v))
    }, [deferredSymbol])

    // Auto-select ATM strike if none selected when market data arrives
    useEffect(() => {
        if (marketData?.atmStrike && marketData.atmStrike > 0) {
            if (dailyCeStrike === 0) handleDailyCeStrikeChange(marketData.atmStrike)
            if (dailyPeStrike === 0) handleDailyPeStrikeChange(marketData.atmStrike)
        }
    }, [marketData?.atmStrike, dailyCeStrike, dailyPeStrike])

    // Available Strikes
    const availableStrikes = useMemo(() => {
        if (marketData?.options && marketData.options.length > 0) {
            return marketData.options.map((o: any) => o.strike).sort((a: number, b: number) => a - b)
        }
        return []
    }, [marketData?.options?.length])

    // 🚀 FREEZE FIX: Only render the ACTIVE tab. No opacity ghosting.
    // Previously: all 3 tabs mounted simultaneously with opacity:0 for inactive tabs.
    // This meant ALL components (CombinedAnalysis + Individual + MarketWatch + StrikeHistory)
    // received data updates and re-rendered even when invisible.
    // Now: only the active tab is in the DOM. Tab switching is wrapped in startTransition
    // so React can interrupt if needed.

    return (
        <React.Fragment>
            <div className="flex flex-col gap-6 p-4 md:p-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
                {/* Header & Tab Switcher */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/50 dark:bg-slate-900/50 p-4 rounded-3xl border border-slate-200/60 dark:border-slate-800/60 backdrop-blur-xl shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl">
                            <BarChart2 size={24} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Market Analysis</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                Deep dive into strikes and flow
                            </p>
                        </div>
                    </div>

                    <div className="flex bg-slate-100/80 dark:bg-slate-800/80 p-1.5 rounded-2xl self-stretch sm:self-auto ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/50">
                        <button
                            onClick={() => startTransition(() => setActiveTab('Bars'))}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                                activeTab === 'Bars'
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] ring-1 ring-slate-200/50 dark:ring-slate-600'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                            }`}
                        >
                            <BarChart2 size={18} strokeWidth={2.5} />
                            Bars
                        </button>
                        <button
                            onClick={() => startTransition(() => setActiveTab('Weekly'))}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                                activeTab === 'Weekly'
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] ring-1 ring-slate-200/50 dark:ring-slate-600'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                            }`}
                        >
                            <CalendarDays size={18} strokeWidth={2.5} />
                            Weekly
                        </button>
                        <button
                            onClick={() => startTransition(() => setActiveTab('Market Watch'))}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                                activeTab === 'Market Watch'
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] ring-1 ring-slate-200/50 dark:ring-slate-600'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                            }`}
                        >
                            <Eye size={18} strokeWidth={2.5} />
                            Market Watch
                        </button>
                    </div>
                </div>

                {/* 🚀 FREEZE FIX: Conditional rendering — only active tab mounts.
                    Each section is wrapped in Suspense with a lightweight skeleton.
                    React.lazy() ensures the component JS bundle loads asynchronously.
                    Combined: page shell renders in <50ms, charts load progressively. */}
                <div className="mt-2">
                    {activeTab === 'Bars' && (
                        <Suspense fallback={<ChartSkeleton />}>
                            {(loading && !marketData) ? <ChartSkeleton /> : (
                                <div className="flex flex-col gap-8">
                                    <CombinedAnalysisSection
                                        symbol={deferredSymbol}
                                        expiry={deferredExpiry || effectiveExpiry}
                                    />
                                    <IndividualAnalysisSection
                                        symbol={deferredSymbol}
                                        expiry={deferredExpiry || effectiveExpiry}
                                    />
                                </div>
                            )}
                        </Suspense>
                    )}

                    {activeTab === 'Weekly' && (
                        <Suspense fallback={<TableSkeleton />}>
                            <div className="flex flex-col gap-8">
                                {/* Header */}
                                <div className="flex items-center gap-4 px-2">
                                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl shadow-sm">
                                        <Activity size={24} strokeWidth={2.5} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                            Daily Strike Analysis
                                        </h3>
                                        <p className="text-sm font-medium text-slate-400 dark:text-slate-500">
                                            Detailed historical drill-down of specific strike performance
                                        </p>
                                    </div>
                                </div>

                                {/* Daily Strike Price History Table */}
                                <div className="bg-white dark:bg-slate-900 rounded-[32px] p-8 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors relative z-20">
                                    <StrikePriceHistoryTable
                                        symbol={deferredSymbol}
                                        selectedCeStrike={dailyCeStrike}
                                        selectedPeStrike={dailyPeStrike}
                                        availableStrikes={availableStrikes}
                                        activeMarketData={deferredMarketData}
                                        onCeStrikeChange={handleDailyCeStrikeChange}
                                        onPeStrikeChange={handleDailyPeStrikeChange}
                                    />
                                </div>
                            </div>
                        </Suspense>
                    )}

                    {activeTab === 'Market Watch' && (
                        <Suspense fallback={<TableSkeleton />}>
                            {(loading && !marketData) ? <TableSkeleton /> : (
                                <MarketWatchSection
                                    symbol={deferredSymbol}
                                    expiry={deferredExpiry || effectiveExpiry}
                                />
                            )}
                        </Suspense>
                    )}
                </div>
            </div>
        </React.Fragment>
    )
}
